import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'next-i18next';

import NumberCounter from '../NumberCounter/NumberCounter';
import VideoIcon from '../VideoIcon';
import BasicLoader from '../BasicLoader';
import InfoPopover from '../InfoPopover/InfoPopover';

import classNames from 'classnames';
import useIntersectionObserver from '../../hooks/useIntersectionObserver';

import { AnimationsActiveContext } from '../../containers/BasePage';

import s from './BigNumberVideoBlock.module.scss';

const TEXT_COLOR_LIGHT = 'text-light';
const TEXT_COLOR_DARK = 'text-dark';

const THEME_PINK_PURPLE = 'pink-purple';
const THEME_PINK_ORANGE = 'pink-orange';
const THEME_BLUE_PURPLE = 'blue-purple';
const THEME_BLUE_GREEN = 'blue-green';

const VIDEO_ICON_GLOBE = 'glob-loop';

const videoIcons = {
  [VIDEO_ICON_GLOBE]: [
    {
      src: `/video/${VIDEO_ICON_GLOBE}--700p--hevc.mp4`,
      type: 'video/mp4; codecs="hvc1"',
    },
    {
      src: `/video/${VIDEO_ICON_GLOBE}--700p--vp9.webm`,
      type: 'video/webm',
    },
  ],
};

const BigNumberVideoBlock = (props) => {
  const {
    titleTop,
    titleBottom,
    value,
    infoTitle,
    infoText,
    infoLink,
    counterAnimationDuration,
    textColor,
    theme,
    videoIcon,
  } = props;
  const hasInfo = !!infoTitle && !!infoText;

  const { t } = useTranslation();
  const [inViewRef, isVisible] = useIntersectionObserver(0.5, undefined, true);
  const [videoIconLoaded, setVideoIconLoaded] = useState(false);

  const videoIconSrcs = videoIcons[videoIcon];
  const color = textColor === TEXT_COLOR_LIGHT ? 'white' : 'black';

  const { animationsActive } = useContext(AnimationsActiveContext) || {};

  const rootCls = classNames(s.Root, 'grid', {
    [s.Visible]: isVisible,
    [s.VideoLoaded]: videoIconLoaded,

    [s.TextColorDark]: textColor === TEXT_COLOR_DARK,

    [s.ThemePinkPurple]: theme === THEME_PINK_PURPLE,
    [s.ThemePinkOrange]: theme === THEME_PINK_ORANGE,
    [s.ThemeBluePurple]: theme === THEME_BLUE_PURPLE,
    [s.ThemeBlueGreen]: theme === THEME_BLUE_GREEN,
  });

  const loaderCls = classNames(s.Loader, {
    [s.Visible]: !videoIconLoaded,
  });

  return (
    <div className={rootCls}>
      <div className={s.Container} ref={inViewRef}>
        <div className={s.StatsContainer}>
          <div className={s.NumberContainer}>
            <h4 className={s.NumberTitle}>
              {titleTop}
              <span className={s.Number}>
                <NumberCounter
                  from={0}
                  to={value}
                  decimalCount={1}
                  duration={animationsActive ? counterAnimationDuration : 0}
                  active={isVisible}
                />
              </span>
              {titleBottom}
            </h4>
            {hasInfo && (
              <div className={s.InfoWrap}>
                <InfoPopover
                  color={color}
                  title={infoTitle}
                  text={infoText}
                  link={infoLink}
                />
              </div>
            )}
          </div>

          <div className={s.VideoContainer}>
            <div className={s.LoaderWrap}>
              <div className={loaderCls}>
                <BasicLoader
                  theme={textColor === 'text-dark' ? 'dark' : 'light'}
                />
              </div>
            </div>

            {videoIconSrcs && isVisible && (
              <VideoIcon
                className={s.VideoIcon}
                active={true}
                loop={true}
                srcs={videoIconSrcs}
                onVideoLoaded={() => {
                  setVideoIconLoaded(true);
                }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

BigNumberVideoBlock.propTypes = {
  titleTop: PropTypes.string,
  titleBottom: PropTypes.string,
  value: PropTypes.number,
  counterAnimationDuration: PropTypes.number,
  textColor: PropTypes.oneOf([TEXT_COLOR_DARK, TEXT_COLOR_LIGHT]),
  theme: PropTypes.oneOf([
    THEME_PINK_PURPLE,
    THEME_PINK_ORANGE,
    THEME_BLUE_PURPLE,
    THEME_BLUE_GREEN,
  ]),
  videoIcon: PropTypes.oneOf([VIDEO_ICON_GLOBE]),
};

BigNumberVideoBlock.defaultProps = {
  titleTop: '',
  titleBottom: '',
  value: 0,
  counterAnimationDuration: 1.6,
  textColor: TEXT_COLOR_LIGHT,
  theme: THEME_PINK_PURPLE,
  videoIcon: VIDEO_ICON_GLOBE,
};
export default BigNumberVideoBlock;
